import { useState, useCallback } from 'react';
import { useGateway } from './useGateway';
import { GetCardsPricingDto, CardsPricingDto } from '../types/card';

// Define the PricingResult type here since we're removing the legacy types
interface PricingResult {
  price: number;
  price_no_discount: number;
  discount: number;
}

/**
 * Hook for pricing operations using the service-based architecture
 * This replaces the legacy usePricing hook
 */
export const usePricingService = () => {
  const { gateway, handleRequest, isLoading, error } = useGateway();
  const [pricing, setPricing] = useState<PricingResult | null>(null);

  /**
   * Get pricing from card ID
   */
  const getPricingFromCardId = useCallback(async (cardId: string, faceValue: number, currency: string): Promise<PricingResult | null> => {
    const result = await handleRequest(() => 
      gateway.pricing.getPricingFromCardId(cardId, faceValue, currency)
    );
    
    if (result) {
      // Transform the new service response to match legacy PricingResult
      const legacyResult: PricingResult = {
        price: result.price,
        price_no_discount: result.price_no_discount,
        discount: result.discount
      };
      setPricing(legacyResult);
      return legacyResult;
    }
    
    return null;
  }, [gateway.pricing, handleRequest]);

  /**
   * Get pricing for multiple cards with optional coupon support
   */
  const getCardsPricing = useCallback(async (query: GetCardsPricingDto): Promise<CardsPricingDto | undefined> => {
    return await gateway.pricing.getCardsPricing(query);
  }, [gateway.pricing]);

  /**
   * Clear pricing data
   */
  const clearPricing = useCallback(() => {
    setPricing(null);
  }, []);

  return {
    pricing,
    isLoading,
    error,
    getPricingFromCardId,
    getCardsPricing,
    clearPricing
  };
};
